// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyProps } from "@insite/mobius/Typography/Typography";
import React, { FC } from "react";
import { connect } from "react-redux";

const enum fields {
    title = "title",
    text = "text",
    buttonText = "buttonText",
    buttonStyle = "buttonStyle",
    isSignInOverlay = "isSignInOverlay",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.text]: string;
        [fields.buttonText]: string;
        [fields.buttonStyle]: "primary" | "secondary";
        [fields.isSignInOverlay]: boolean;
    };
}

export const mapStateToProps = (state: ApplicationState) => ({
    creditTermsExternalURL: (getSettingsCollection(state).customSettings as any)?.creditTermsExternalURL,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface ApplyForCreditStyles {
    container?: GridContainerProps;
    titleGridItem?: GridItemProps;
    textGridItem?: GridItemProps;
    buttonGridItem?: GridItemProps;
    titleStyle?: TypographyProps;
    textStyle?: TypographyProps;
    buttonStyle?: ButtonPresentationProps;
}

export const applyForCreditStyles: ApplyForCreditStyles = {};

const styles = applyForCreditStyles;

const ApplyForCredit: FC<Props> = ({ fields, creditTermsExternalURL }) => {
    const handleApplyCredit = () => {
        window.location.href = creditTermsExternalURL;
    };

    return (
        <GridContainer {...styles.container}>
            <GridItem {...styles.titleGridItem}>
                <Typography {...styles.titleStyle}>{fields.title}</Typography>
            </GridItem>
            <GridItem {...styles.textGridItem}>
                <Typography {...styles.textStyle}>{fields.text}</Typography>
            </GridItem>
            {creditTermsExternalURL && (
                <GridItem {...styles.buttonGridItem}>
                    <Button
                        onClick={handleApplyCredit}
                        data-test-selector="signInApplyForCridet_applyButton"
                        aria-label="Apply For Cridet"
                        {...styles.buttonStyle}
                        variant={fields.buttonStyle}
                        sizeVariant={fields.isSignInOverlay ? "small" : "medium"}
                    >
                        {fields.buttonText}
                    </Button>
                </GridItem>
            )}
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(ApplyForCredit),
    definition: {
        allowedContexts: ["SignInPage", "Header", "CreateAccountPage"],
        group: "Common",
        icon: "User",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "Apply for Credit Terms",
                fieldType: "Translatable",
                sortOrder: 0,
            },
            {
                name: fields.text,
                displayName: "Main Text",
                editorTemplate: "MultilineTextField",
                defaultValue: "Please allow 1-3 business days for processing.",
                fieldType: "Translatable",
                sortOrder: 1,
            },
            {
                name: fields.buttonText,
                editorTemplate: "TextField",
                defaultValue: "Apply Today",
                fieldType: "Translatable",
                sortOrder: 3,
            },
            {
                name: fields.buttonStyle,
                editorTemplate: "DropDownField",
                fieldType: "Contextual",
                options: [
                    {
                        displayName: "Primary",
                        value: "primary",
                    },
                    { displayName: "Secondary", value: "secondary" },
                ],
                hideEmptyOption: true,
                defaultValue: "primary",
                sortOrder: 4,
            },
            {
                name: fields.isSignInOverlay,
                displayName: "Is Sign In Overlay",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 5,
            },
        ],
    },
};

export default widgetModule;
